import { prettyEnglishDateTime, prettyEnglishDateTimeTzFull } from "@app/date";
import {
  calcMetrics,
  formatCurrency,
  getEndpointUrl,
  getStackType,
  getStackTypeTitle,
  selectAppById,
  selectDatabaseById,
  selectDatabaseImageById,
  selectEndpointById,
  selectEnvironmentById,
  selectServiceById,
  selectStackById,
} from "@app/deploy";
import { useSelector } from "@app/react";
import {
  appDetailUrl,
  appServiceUrl,
  databaseDetailUrl,
  endpointDetailUrl,
  environmentDetailUrl,
  stackDetailEnvsUrl,
} from "@app/routes";
import type { DeployDatabase, DeployService } from "@app/types";
import { Link } from "react-router-dom";
import { Code } from "../code";
import { CopyTextButton } from "../copy";
import { Pill, type PillVariant } from "../pill";
import { tokens } from "../tokens";
import { Tooltip } from "../tooltip";

export const CodeCell = ({ value }: { value: string }) => {
  return (
    <Tooltip text={value} fluid placement="top">
      <Code className="text-ellipsis whitespace-nowrap max-w-[30ch] overflow-hidden inline-block">
        {value}
      </Code>
    </Tooltip>
  );
};

export const DataTableCell = ({
  label,
  description,
  href,
  tooltip,
  icon,
}: {
  label: React.ReactNode;
  description?: React.ReactNode;
  href?: string;
  tooltip?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}) => {
  let labelElement = <span>{label}</span>;

  if (href) {
    labelElement = (
      <Link to={href} className={tokens.type["table link"]}>
        {labelElement}
      </Link>
    );
  }

  if (tooltip) {
    labelElement = (
      <Tooltip text={tooltip} fluid>
        <span className="border-b border-dotted border-gray-400">
          {labelElement}
        </span>
      </Tooltip>
    );
  }

  return (
    <div className="flex flex-row items-center truncate">
      {icon}
      <div className="flex flex-col flex-grow">
        <div className={tokens.type.darker}>{labelElement}</div>
        {description && (
          <div className={tokens.type["normal lighter"]}>{description}</div>
        )}
      </div>
    </div>
  );
};

export const AppCell = ({
  id,
  hideIcon = false,
}: {
  id: string;
  hideIcon?: boolean;
}) => {
  const app = useSelector((s) => selectAppById(s, { id: id }));
  return (
    <DataTableCell
      label={app.handle}
      href={appDetailUrl(app.id)}
      icon={
        hideIcon ? null : (
          <img
            src="/resource-types/logo-app.png"
            className="w-[32px] h-[32px] mr-2 align-middle"
            aria-label="App"
          />
        )
      }
    />
  );
};

export const EnvironmentCell = ({
  id,
  hideIcon = false,
}: {
  id: string;
  hideIcon?: boolean;
}) => {
  const env = useSelector((s) => selectEnvironmentById(s, { id: id }));
  const stack = useSelector((s) => selectStackById(s, { id: env.stackId }));
  return (
    <DataTableCell
      label={env.handle}
      description={`${stack.organizationId ? "Dedicated Stack " : "Shared Stack "}(${stack.region})`}
      href={environmentDetailUrl(env.id)}
      icon={
        hideIcon ? null : (
          <img
            src="/resource-types/logo-environment.png"
            className="w-[32px] h-[32px] mr-2 align-middle"
            aria-label="Environment"
          />
        )
      }
    />
  );
};

export const StackCell = ({
  id,
  hideIcon = false,
}: {
  id: string;
  hideIcon?: boolean;
}) => {
  const stack = useSelector((s) => selectStackById(s, { id }));

  const stackType = getStackType(stack);
  let stackIcon = "/resource-types/logo-stack.png";
  if (stackType === "self_hosted") {
    stackIcon = "/resource-types/logo-aws.png";
  } else if (stackType === "dedicated") {
    stackIcon = "/resource-types/logo-dedicated-stack.png";
  }

  return (
    <DataTableCell
      label={stack.name}
      description={`${getStackTypeTitle(stack)} (${stack.region})`}
      href={stackDetailEnvsUrl(stack.id)}
      icon={
        hideIcon ? null : (
          <img
            src={stackIcon}
            className="w-[32px] h-[32px] mr-2 align-middle"
            aria-label="Stack"
          />
        )
      }
    />
  );
};

export const ServicesCell = ({ services }: { services: DeployService[] }) => {
  const metrics = calcMetrics(services);
  return (
    <DataTableCell
      label={`${services.length} Service${services.length === 1 ? "" : "s"}`}
      description={`${metrics.totalMemoryLimit / 1024} GB · ${metrics.totalCPU} CPU`}
    />
  );
};

export const CurrencyCell = ({
  value,
  decimalPlaces = 2,
}: {
  value: number;
  decimalPlaces?: number;
}) => {
  return <DataTableCell label={formatCurrency(value, decimalPlaces)} />;
};

export const PillCell = ({
  value,
  label,
  valueToVariantMap,
}: {
  value: string;
  label?: string;
  valueToVariantMap: Record<string, PillVariant>;
}) => {
  return <Pill variant={valueToVariantMap[value]}>{label || value}</Pill>;
};

export const DateTimeCell = ({ date }: { date: string }) => {
  return (
    <DataTableCell
      label={prettyEnglishDateTime(date)}
      tooltip={prettyEnglishDateTimeTzFull(date, "local")}
    />
  );
};

const DatabaseIconForCell = ({ db }: { db: DeployDatabase }) => (
  <img
    src={`/database-types/logo-${db.type}.png`}
    className="w-[32px] h-[32px] mr-2 mt-1 align-middle"
    aria-label={`${db.type} Database`}
  />
);

export const DatabaseCell = ({
  id,
  hideIcon = false,
}: { id: string; hideIcon?: boolean }) => {
  const db = useSelector((s) => selectDatabaseById(s, { id }));
  const image = useSelector((s) =>
    selectDatabaseImageById(s, { id: db.databaseImageId }),
  );

  return (
    <DataTableCell
      label={db.handle}
      href={databaseDetailUrl(db.id)}
      description={image.description}
      icon={hideIcon ? null : <DatabaseIconForCell db={db} />}
    />
  );
};

export const ServiceCell = ({
  id,
  hideIcon = false,
}: {
  id: string;
  hideIcon?: boolean;
}) => {
  const service = useSelector((s) => selectServiceById(s, { id }));
  const app = useSelector((s) => selectAppById(s, { id: service.appId }));
  const db = useSelector((s) =>
    selectDatabaseById(s, { id: service.databaseId }),
  );
  const link = service.appId
    ? appServiceUrl(service.appId, service.id)
    : databaseDetailUrl(service.databaseId);
  let icon = (
    <img
      src="/resource-types/logo-service.png"
      className="w-[32px] h-[32px] mr-2 align-middle"
      aria-label="Service"
    />
  );
  if (db.handle) icon = <DatabaseIconForCell db={db} />;
  return (
    <DataTableCell
      label={
        app.handle
          ? `${app.handle} / ${service.processType}`
          : service.processType
      }
      href={link}
      icon={hideIcon ? null : icon}
    />
  );
};

export const EndpointCell = ({ id }: { id: string }) => {
  const endpoint = useSelector((s) => selectEndpointById(s, { id }));
  return (
    <DataTableCell
      label={
        <div className="flex">
          <span>{getEndpointUrl(endpoint)}</span>
          <CopyTextButton text={getEndpointUrl(endpoint)} className="ml-2" />
        </div>
      }
      href={endpointDetailUrl(endpoint.id)}
      icon={
        <img
          src="/resource-types/logo-vhost.png"
          className="w-[32px] h-[32px] mr-2 align-middle"
          aria-label="Endpoint"
        />
      }
    />
  );
};
